import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ICreateGroup,
  ServerCodes,
  ToastTypes,
  ErrorsType,
  GlobalToastTypeNames,
} from 'types';

import { useAddGroupMutation, CustomErrorType } from 'core/graphql';

import { useToasts } from 'core/hooks';
import { IAddGroupPopupValues } from 'molecules';

import { GroupsQuery, SettingsMetaQuery } from 'core/operations/queries';

import { groupErrorReducer, getGroupErrors } from './reducer';
import { ILessonTimeDay, lessonTimeFormErrors } from './lessonTimeErrors';
import { useGroupPermissions } from './useGroupPermissions';

type ICreateGroupHookType = [
  (group: ICreateGroup) => void,
  {
    success: boolean;
    loading: boolean;
    errors: ErrorsType<IAddGroupPopupValues> | null;
  },
];

export const useCreateGroup = (): ICreateGroupHookType => {
  const { t } = useTranslation();
  // Те же переменные, что у самих списков: поля и счётчики, закрытые роли,
  // не перезапрашиваются (иначе — отказ поля, ERP backlog-7).
  const { canReadGroupStaffFields, canReadGroupCounts } = useGroupPermissions();
  const [addGroupMutation, { data, loading }] = useAddGroupMutation({
    refetchQueries: [
      {
        query: GroupsQuery,
        variables: {
          first: 10,
          withStaffFields: canReadGroupStaffFields,
        },
      },
      {
        query: SettingsMetaQuery,
        variables: { withGroups: canReadGroupCounts },
      },
    ],
  });
  const [errors, dispatch] = useReducer(groupErrorReducer, null);
  const { addToast } = useToasts();
  /** Дни последней отправки — чтобы подсветить «конец» у нужного (F8). */
  const sentDays = useRef<ILessonTimeDay[]>([]);

  useEffect(() => {
    const serverErrors = data?.createGroup?.customErrors;
    if (serverErrors && serverErrors.length) {
      // Перепроверка F8: длительность занятия — под «концом», а не тостом
      // «Что-то пошло не так».
      const lessonTime = lessonTimeFormErrors(serverErrors, sentDays.current);
      if (lessonTime) {
        dispatch({
          type: ServerCodes.lessonTime,
          payload: (lessonTime as unknown) as ErrorsType<IAddGroupPopupValues>,
        });
        return;
      }
      const errorsFound = getGroupErrors(serverErrors as CustomErrorType[]);

      switch (true) {
        case errorsFound.groupExist:
          dispatch({ type: ServerCodes.groupExist });
          break;
        case errorsFound.lessonExist:
          dispatch({ type: ServerCodes.lessonExist });
          break;

        default:
          addToast({
            data: {
              title: `${t('common.somethingWentWrong')}`,
              type: ToastTypes.error,
            },
            type: GlobalToastTypeNames.GroupAdd,
          });
      }
    }
  }, [data?.createGroup?.customErrors, addToast, t]);

  const createGroup = useCallback(
    (group: ICreateGroup) => {
      sentDays.current = group.groupInput.daysOfClass || [];
      addGroupMutation({
        variables: {
          groupInput: {
            name: group.groupInput.name,
            price: group.groupInput.price,
            currency: group.groupInput.currency,
            startDate: group.groupInput.startDate,
            course_id: group.groupInput.course_id,
            daysOfClass: group.groupInput.daysOfClass,
          },
          moduleInput: group.moduleInput,
        },
      });
    },
    [addGroupMutation],
  );

  return [
    createGroup,
    {
      success: data?.createGroup?.success || false,
      loading,
      errors,
    },
  ];
};
